import { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useTheme } from '@/contexts/ThemeContext';
import DashboardLayout from '@/components/DashboardLayout';
import { DollarSign, Eye, MousePointer, Target, Activity, TrendingUp, Zap } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RANGES = [
  { label:'Hoy', value:'today' },{ label:'Ayer', value:'yesterday' },
  { label:'7d', value:'7d' },{ label:'15d', value:'15d' },
  { label:'30d', value:'30d' },{ label:'90d', value:'90d' },
];
function chileDate(d){const c=new Date(d);c.setHours(c.getHours()-4);return c.toISOString().slice(0,10);}
function getDateRange(key){
  const to=new Date(),from=new Date();
  if(key==='today')return{from:chileDate(to),to:chileDate(to)};
  if(key==='yesterday'){const y=new Date(to);y.setDate(y.getDate()-1);return{from:chileDate(y),to:chileDate(y)};}
  const days={'7d':7,'15d':15,'30d':30,'90d':90}[key]||30;
  from.setDate(from.getDate()-days);
  return{from:chileDate(from),to:chileDate(to)};
}
const METRICS=[
  {key:'spent',name:'Gasto',icon:DollarSign,fmt:(v,currency)=>{const c=currency||'USD';const sym=c==='CLP'?'$':c==='EUR'?'€':c==='MXN'?'MX$':'$';return v>=1000000?`${sym}${(v/1000000).toFixed(1)}M`:v>=1000?`${sym}${(v/1000).toFixed(1)}K`:`${new Intl.NumberFormat('es-CL').format(Math.round(v))} ${c}`},color:'#1877F2',winner:'lower'},
  {key:'impressions',name:'Impresiones',icon:Eye,fmt:v=>v>=1e6?`${(v/1e6).toFixed(1)}M`:`${(v/1000).toFixed(0)}K`,color:'#8b5cf6',winner:'higher'},
  {key:'clicks',name:'Clicks',icon:MousePointer,fmt:v=>v>=1000?`${(v/1000).toFixed(1)}K`:v.toLocaleString(),color:'#10b981',winner:'higher'},
  {key:'conversions',name:'Conversiones',icon:Target,fmt:v=>v>=1000?`${(v/1000).toFixed(1)}K`:v.toLocaleString(),color:'#f59e0b',winner:'higher'},
  {key:'ctr',name:'CTR',icon:Activity,fmt:v=>`${v.toFixed(2)}%`,color:'#06b6d4',winner:'higher'},
  {key:'roi',name:'ROI',icon:TrendingUp,fmt:v=>`${v.toFixed(2)}x`,color:'#ec4899',winner:'higher'},
];
export default function Comparison(){
  const{token}=useContext(AuthContext);
  const{clientId}=useWorkspace();
  const clientIdRef=useRef(clientId);
  const theme=useTheme();
  const[data,setData]=useState(null);
  const[loading,setLoading]=useState(true);
  const[range,setRange]=useState('30d');
  const[aiAnalysis,setAiAnalysis]=useState('');
  const[aiLoading,setAiLoading]=useState(false);
  useEffect(()=>{clientIdRef.current=clientId;},[clientId]);
  useEffect(()=>{fetchData(range);},[clientId,range]); // eslint-disable-line
  const fetchData=async(r)=>{
    setLoading(true);setAiAnalysis('');
    try{
      const cid=clientIdRef.current;
      const{from,to}=getDateRange(r);
      const cidParam=cid?`&client_id=${cid}`:'';
      const rangeMap={'Hoy':'today','Ayer':'yesterday','Últimos 7 días':'7d','Últimos 15 días':'15d','Últimos 30 días':'30d','Últimos 90 días':'90d'};
      const presetKey=r;
      // FIX: ambas plataformas vienen de metrics_daily (no mas /meta/campaigns-live)
      const dbRes = await axios.get(
        `${API}/dashboard/metrics?date_from=${from}&date_to=${to}${cidParam}`,
        {headers:{Authorization:`Bearer ${token}`}}
      ).catch(()=>({data:{campaigns:[]}}));
      const allCamps = dbRes.data?.campaigns || [];
      const metaCamps = allCamps.filter(c => c.platform === 'meta');
      const googleCamps = allCamps.filter(c => c.platform === 'google');
      const sum=(arr,key)=>arr.reduce((s,c)=>s+(parseFloat(c[key])||0),0);
      const avg=(arr,key)=>arr.length?sum(arr,key)/arr.length:0;
      const metaCurrency=metaCamps[0]?.currency||'USD';
      const googleCurrency=googleCamps[0]?.currency||'CLP';
      setData({
        meta:{campaigns:metaCamps.length,spent:sum(metaCamps,'spent'),impressions:sum(metaCamps,'impressions'),clicks:sum(metaCamps,'clicks'),conversions:sum(metaCamps,'conversions'),ctr:avg(metaCamps,'ctr'),roi:avg(metaCamps,'roi'),currency:metaCurrency},
        google:{campaigns:googleCamps.length,spent:sum(googleCamps,'spent'),impressions:sum(googleCamps,'impressions'),clicks:sum(googleCamps,'clicks'),conversions:sum(googleCamps,'conversions'),ctr:avg(googleCamps,'ctr'),roi:avg(googleCamps,'roi'),currency:googleCurrency},
      });
    }catch(e){console.error(e);}
    finally{setLoading(false);}
  };
  const runAI=async()=>{
    if(!data)return;
    setAiLoading(true);setAiAnalysis('');
    try{
      const res=await axios.post(`${API}/ai/compare-platforms`,{meta:data.meta,google:data.google,range},{headers:{Authorization:`Bearer ${token}`}});
      setAiAnalysis(res.data?.analysis||'');
    }catch(e){
      const m=data.meta,g=data.google;
      const cpaM=m.conversions>0?m.spent/m.conversions:0;
      const cpaG=g.conversions>0?g.spent/g.conversions:0;
      const better=cpaM<cpaG?'Meta Ads':'Google Ads';
      const worse=cpaM<cpaG?'Google Ads':'Meta Ads';
      setAiAnalysis(`📊 Análisis (${range})\n\n✅ ${better} tiene mejor CPA ($${Math.min(cpaM,cpaG).toFixed(0)} vs $${Math.max(cpaM,cpaG).toFixed(0)})\n\n📈 Google CTR: ${g.ctr.toFixed(2)}% vs Meta CTR: ${m.ctr.toFixed(2)}%\n\n💰 Meta: ${m.conversions.toFixed(0)} conv | Google: ${g.conversions.toFixed(0)} conv\n\n🎯 Recomendación: Aumenta presupuesto en ${better} y optimiza creativos en ${worse}.`);
    }
    setAiLoading(false);
  };
  const fmtTick=(v)=>v>=1e6?`${(v/1e6).toFixed(1)}M`:v>=1000?`${(v/1000).toFixed(0)}K`:String(v);
  return(
    <DashboardLayout hideDatePicker>
      <div className="p-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-black text-gray-900 tracking-tight">Comparación</h1>
            <p className="text-sm text-gray-400 mt-1">Meta Ads vs Google Ads</p>
          </div>
          <div className="flex gap-2">
            {RANGES.map(r=>(
              <button key={r.value} onClick={()=>setRange(r.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${range===r.value?'bg-blue-600 text-white':'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                {r.label}
              </button>
            ))}
          </div>
        </div>
        {loading?(
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"/>
          </div>
        ):!data?(
          <div className="text-center py-16 text-gray-400">No hay datos disponibles</div>
        ):(
          <>
            <div className="grid grid-cols-2 gap-4 mb-6">
              {[{key:'meta',name:'Meta Ads',color:'#1877F2',icon:'f'},{key:'google',name:'Google Ads',color:'#EA4335',icon:'G'}].map(p=>(
                <div key={p.key} className="bg-white rounded-2xl p-6 border-2 shadow-sm" style={{borderColor:p.color+'40'}}>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center font-black text-white text-lg" style={{background:p.color}}>{p.icon}</div>
                    <div>
                      <p className="font-bold text-gray-900">{p.name}</p>
                      <p className="text-xs text-gray-400">{data[p.key].campaigns} campañas</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {METRICS.map(m=>(
                      <div key={m.key}>
                        <p className="text-xs text-gray-400 mb-0.5 font-medium">{m.name}</p>
                        <p className="text-xl font-black text-gray-900">{m.fmt(data[p.key][m.key]||0, p.key==='meta'?data.meta.currency:data.google.currency)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 mb-6 border border-blue-100">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-blue-600"/>
                  <h3 className="font-bold text-gray-900">Análisis IA</h3>
                </div>
                <button onClick={runAI} disabled={aiLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-all">
                  {aiLoading?<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>:<Zap className="w-4 h-4"/>}
                  {aiLoading?'Analizando...':'⚡ Analizar con IA'}
                </button>
              </div>
              {aiAnalysis?(
                <div className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap bg-white rounded-xl p-4 border border-blue-100">{aiAnalysis}</div>
              ):(
                <p className="text-sm text-gray-400">Haz clic en "Analizar con IA" para obtener recomendaciones basadas en tus datos reales.</p>
              )}
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-100 mb-6">
              <h3 className="font-bold text-gray-900 mb-4">Comparación visual</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={[
                  {name:'Gasto',Meta:data.meta.spent,Google:data.google.spent},
                  {name:'Clicks',Meta:data.meta.clicks,Google:data.google.clicks},
                  {name:'Conversiones',Meta:data.meta.conversions,Google:data.google.conversions},
                ]} barGap={4}>
                  <CartesianGrid strokeDasharray="4 4" stroke="#f0f0f0" vertical={false}/>
                  <XAxis dataKey="name" tick={{fontSize:12,fill:'#94a3b8'}} axisLine={false} tickLine={false}/>
                  <YAxis tick={{fontSize:11,fill:'#94a3b8'}} axisLine={false} tickLine={false} tickFormatter={fmtTick} width={42}/>
                  <Tooltip contentStyle={{borderRadius:10,border:'1px solid #e5e7eb',boxShadow:'0 4px 12px rgba(0,0,0,0.08)'}} formatter={(v,n)=>[fmtTick(v),n]}/>
                  <Bar dataKey="Meta" fill="#1877F2" radius={[6,6,0,0]} barSize={32}/>
                  <Bar dataKey="Google" fill="#EA4335" radius={[6,6,0,0]} barSize={32}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="font-bold text-gray-900">Ganador por métrica</h3>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    {['Métrica','Meta Ads','Google Ads','Ganador'].map(h=>(
                      <th key={h} className="px-5 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-400">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {METRICS.map((m,i)=>{
                    const mv=data.meta[m.key]||0,gv=data.google[m.key]||0;
                    const winner=m.winner==='higher'?(mv>gv?'meta':gv>mv?'google':'tie'):(mv<gv?'meta':gv<mv?'google':'tie');
                    return(
                      <tr key={m.key} className={`border-b border-gray-50 ${i%2===0?'bg-white':'bg-gray-50/50'}`}>
                        <td className="px-5 py-3 font-semibold text-gray-700">
                          <div className="flex items-center gap-2"><m.icon className="w-4 h-4" style={{color:m.color}}/>{m.name}</div>
                        </td>
                        <td className={`px-5 py-3 font-bold ${winner==='meta'?'text-blue-600':'text-gray-500'}`}>{m.fmt(mv, data.meta.currency)}</td>
                        <td className={`px-5 py-3 font-bold ${winner==='google'?'text-red-500':'text-gray-500'}`}>{m.fmt(gv, data.google.currency)}</td>
                        <td className="px-5 py-3">
                          {winner==='tie'?<span className="text-gray-400 text-xs">Empate</span>:
                            <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${winner==='meta'?'bg-blue-100 text-blue-700':'bg-red-100 text-red-700'}`}>
                              {winner==='meta'?'Meta Ads':'Google Ads'}
                            </span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
